import styled, { css, keyframes } from 'styled-components';

import { Text } from '@/nova/components';
import { COLORS, MOBILE, SHOWCASE_ANIM } from '@/nova/theme/tokens';
import { hoverWash } from '@/nova/theme/mixins';

/* A talking-point card: a collapsed pill (title only) that morphs into an expanded
 * card revealing the typed-out description. Re-skinned onto the product page's
 * standard card surface (COLORS.surfaceCard + the shared hoverWash). The hairline
 * border keeps the card visible against the panel in BOTH themes (on light, surface
 * and panel both go white, so the card needs an edge to read as a card). The collapsed
 * radius is a 40px-tall pill (20px) and the expanded radius is 16px, so the morph
 * never sweeps through a giant corner radius. */
export const Card = styled.div<{ $active: boolean }>`
  background-color: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
  border-radius: 20px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  height: 40px;
  box-sizing: border-box;
  will-change: height, width, border-radius;
  display: inline-flex;
  flex-direction: column;
  transition:
    height ${SHOWCASE_ANIM.MORPH_MS}ms ${SHOWCASE_ANIM.EASE},
    width ${SHOWCASE_ANIM.MORPH_MS}ms ${SHOWCASE_ANIM.EASE},
    border-radius ${SHOWCASE_ANIM.MORPH_MS}ms ease,
    background-color ${SHOWCASE_ANIM.MORPH_MS}ms ease;

  ${({ $active }): ReturnType<typeof css> | false =>
    $active &&
    css`
      border-radius: 16px;
      z-index: 10;
    `}

  ${hoverWash}
`;

export const Inner = styled.div<{ $active: boolean }>`
  position: relative;
  padding: 8px 12px 8px 8px;
  box-sizing: border-box;
  transition: padding ${SHOWCASE_ANIM.MORPH_MS}ms ${SHOWCASE_ANIM.EASE};
  ${({ $active }): string => ($active ? 'padding: 16px;' : '')}

  ${MOBILE} {
    ${({ $active }): string => ($active ? 'padding: 10px;' : '')}
  }
`;

export const Header = styled.div<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  height: 24px;
  gap: 10px;
  /* Bound to the card width so a long collapsed title ellipsizes instead of being
   *  hard-clipped at the pill edge. */
  max-width: 100%;
  white-space: nowrap;

  ${({ $active }): ReturnType<typeof css> | false =>
    $active &&
    css`
      align-items: flex-start;
      width: 100%;
      height: auto;
      white-space: normal;
    `}
`;

// Leading slot when collapsed (the "+" affordance). When the card expands this is
// swapped for the Control (countdown ring / collapse button) in the same position.
export const IconWrap = styled.span`
  flex: 0 0 auto;
  display: flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${COLORS.surfaceFaint};
`;

export const Title = styled(Text)<{ $active: boolean }>`
  font-size: 16px;
  font-weight: 500;
  color: ${COLORS.heading};
  flex: 1 1 auto;
  min-width: 0;

  ${({ $active }): ReturnType<typeof css> =>
    $active
      ? css`
          white-space: normal;
          word-break: break-word;
          overflow: visible;
          line-height: 1.25;
        `
      : css`
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `}
`;

export const Body = styled.div<{ $active: boolean }>`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${({ $active }): ReturnType<typeof css> =>
    $active
      ? css`
          opacity: 1;
          transform: translateY(0);
          transition:
            opacity ${SHOWCASE_ANIM.MORPH_MS}ms ease ${SHOWCASE_ANIM.MORPH_MS}ms,
            transform ${SHOWCASE_ANIM.MORPH_MS}ms ease ${SHOWCASE_ANIM.MORPH_MS}ms;
        `
      : css`
          opacity: 0;
          transform: translateY(8px);
          transition:
            opacity ${SHOWCASE_ANIM.COLLAPSE_MS}ms ease,
            transform ${SHOWCASE_ANIM.COLLAPSE_MS}ms ease;
        `}
`;

export const Description = styled(Text)`
  font-size: 14px;
  line-height: 1.5;
  color: ${COLORS.featureName};
`;

// --- Expanded-card leading control (autoplay countdown ring / collapse button) ----
// Sits in the same leading slot as the collapsed "+" (before the title).
export const Control = styled.span`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const RING_SIZE = 24;
const RING_STROKE = 2.5;
export const RING_R = (RING_SIZE - RING_STROKE) / 2;
const RING_C = 2 * Math.PI * RING_R;

/** Empties the ring (full -> 0) over the dwell, so it reads as a depleting timer. */
const deplete = keyframes`
  from { stroke-dashoffset: 0; }
  to { stroke-dashoffset: ${RING_C}; }
`;

export const RingSvg = styled.svg`
  display: block;
`;

export const RingTrack = styled.circle`
  fill: none;
  stroke: ${COLORS.surfaceMuted};
  stroke-width: ${RING_STROKE};
`;

export const RingProgress = styled.circle<{ $durationMs: number }>`
  fill: none;
  stroke: ${COLORS.heading};
  stroke-width: ${RING_STROKE};
  stroke-linecap: round;
  stroke-dasharray: ${RING_C};
  transform: rotate(-90deg);
  transform-origin: center;
  animation: ${deplete} ${({ $durationMs }): number => $durationMs}ms linear forwards;
`;

export const MinusButton = styled.button`
  flex: 0 0 auto;
  width: ${RING_SIZE}px;
  height: ${RING_SIZE}px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: ${COLORS.surfaceFaint};
  color: ${COLORS.textSubtle};
  cursor: pointer;
`;
